import { useIntl } from 'react-intl'
import { FlightsClassTypes, isRewardFareClass, type FareClassWithReward } from 'constants/flight'
import { toBaseFareClass } from 'lib/flights/rewardFareClasses'
import { useCharterFlightPrice } from 'hooks/useFlightPrice'
import { MALDIVES_CHARTER_ROUTE } from 'api/lib/flightDealMap'
import Group from 'components/utils/Group'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Caption from 'LuxKit/Typography/Caption'
import PriceRowCaption from 'LuxKit/PricePoints/PriceRowCaption'
import FormatCurrency from 'components/Common/FormatCurrency/FormatCurrency'
import FormatLoyaltyPoints from 'loyalty/components/Formatters/FormatLoyaltyPoints'

/** Per-person reward rates for non-member preview pricing. */
const REWARD_RATE_BY_BASE: Record<string, { pointsPerPerson: number; cashPerPerson: number }> = {
  [FlightsClassTypes.ECONOMY]: { pointsPerPerson: 120_000, cashPerPerson: 199 },
  [FlightsClassTypes.PREMIUM]: { pointsPerPerson: 200_000, cashPerPerson: 299 },
  [FlightsClassTypes.BUSINESS]: { pointsPerPerson: 500_000, cashPerPerson: 699 },
}

interface Props {
  checkInDate?: string
  checkOutDate?: string
  occupancies: Array<App.Occupants>
  fareClass: FareClassWithReward
  /** Duration used for "from $X" pricing when trip dates are not selected. PR 2 wires this to per-date pricing hooks. */
  bundleNightsForCheapestWithoutDates?: number
  /** When true, skip reward availability request and show hardcoded preview pricing instead. */
  rewardPreviewOnly?: boolean
}

/**
 * Renders the "from" pricing line for a fare class card in the Maldives charter modal.
 *
 * Cash variants show `From <amount> /adult` (single horizontal row).
 * Reward variants show `From` on its own line, then `<points> + <cash> /person` below.
 */
function MaldivesCharterFareClassModalBundleFromPrice({
  checkInDate,
  checkOutDate,
  occupancies,
  fareClass,
  bundleNightsForCheapestWithoutDates: _bundleNightsForCheapestWithoutDates,
  rewardPreviewOnly = false,
}: Props) {
  const intl = useIntl()
  const isReward = isRewardFareClass(fareClass)

  const {
    adultPrice,
    fetching,
    rewardSeatPointsPerPerson,
    rewardSeatCashCoPayPerPerson,
    rewardAvailable,
  } = useCharterFlightPrice({
    charterBundleRoute: MALDIVES_CHARTER_ROUTE,
    checkInDate,
    checkOutDate,
    occupancies,
    fareClass,
    disabled: rewardPreviewOnly && isReward,
  })

  const fromLabel = intl.formatMessage({
    id: 'RUJ7Gofk5bJQT5Frz6g8',
    defaultMessage: 'From',
    description: 'Pricing label prefix before fare class price difference in charter flight modal',
  })
  const perAdultSuffix = intl.formatMessage({
    id: 'BUTmxzV-isuEf_HqftDr',
    defaultMessage: '/adult',
    description: 'Suffix after bundle “from” price in Maldives charter fare-class modal (per adult traveller)',
  })
  const perPersonSuffix = intl.formatMessage({
    id: 'ew2ARy7ro3tFSM4inpHN',
    defaultMessage: '/person',
    description: 'Per person pricing unit suffix in Maldives calendar',
  })
  const loadingLabel = intl.formatMessage({
    id: 'C6m8UbIIgveZbgrQtJ6T',
    defaultMessage: 'Loading...',
    description: 'Loading state text while charter flight prices are being fetched',
  })

  if (fetching) {
    return (
      <Caption variant="large" weight="normal" colour="neutral-two">
        {loadingLabel}
      </Caption>
    )
  }

  if (isReward) {
    const rate = REWARD_RATE_BY_BASE[toBaseFareClass(fareClass)]
    const previewPoints = rewardPreviewOnly && rate ? rate.pointsPerPerson : undefined
    const previewCash = rewardPreviewOnly && rate ? rate.cashPerPerson : undefined
    const displayPoints = previewPoints ?? rewardSeatPointsPerPerson
    const displayCash = previewCash ?? rewardSeatCashCoPayPerPerson

    if (!rewardPreviewOnly && (!rewardAvailable || rewardSeatPointsPerPerson === undefined)) {
      return null
    }
    if (displayPoints === undefined) {
      return null
    }
    return (
      <VerticalSpacer gap={4}>
        <PriceRowCaption>{fromLabel}</PriceRowCaption>
        <Group direction="horizontal" gap={4} verticalAlign="baseline" wrap="wrap">
          <Caption variant="large" weight="bold" colour="neutral-one">
            <FormatLoyaltyPoints points={displayPoints} abbreviated />
            {displayCash !== undefined && displayCash > 0 && <>
              {' + '}
              <FormatCurrency value={displayCash} format="roundedDollar" />
            </>}
          </Caption>
          <Caption variant="medium" colour="neutral-three" weight="normal">
            {perPersonSuffix}
          </Caption>
        </Group>
      </VerticalSpacer>
    )
  }

  if (!adultPrice) {
    return null
  }

  return (
    <Group direction="horizontal" gap={4} verticalAlign="baseline">
      <PriceRowCaption>{fromLabel}</PriceRowCaption>
      <Caption variant="large" weight="bold" colour="neutral-one">
        <FormatCurrency value={adultPrice} format="roundedDollar" />
      </Caption>
      <Caption variant="medium" colour="neutral-three" weight="normal">
        {perAdultSuffix}
      </Caption>
    </Group>
  )
}

export default MaldivesCharterFareClassModalBundleFromPrice
